import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { QueryResult, useMutation } from '@apollo/client';
import {
  AgFinancing,
  AgFinancingDocumentType,
  AgFinancingFileFolder,
  AgFinancingFilesContext,
  AgFinancingFileStatus
} from '@indigo-ag/schema';
import {
  Button,
  ContentCard,
  palettes,
  RegularTitle,
  SmallBody,
  Body,
  TableActions,
  useFlashMessage
} from '@indigo-ag/component-module';
import styled from 'styled-components';

import i18next from 'core/i18n';
import { NewPage } from 'core/components';
import { LoadingWrapper, TABLE_ACTIONS, text, UploadDocumentsCustomActions } from 'shared';
import { negotiationInformation } from '../../components/UploadDocumentsV2/queries/uploadDocuments.queries';
import { UploadDocuments } from 'features/brazil-grower/components/UploadDocumentsV2/components/UploadDocuments/UploadDocuments';
import {
  DocumentsToUploadList,
  DocumentToUpload,
  DocumentToUploadColumnId
} from 'features/brazil-grower/components/UploadDocumentsV2/components/DocumentsToUploadList/DocumentsToUploadList';
import { Dictionary } from 'lodash';
import { RejectedPromiseReason, uploadFilesToAgFinancing } from '../../components/BrazilGrowerFinance/utils';
import { createAgFinancingFile } from '../../queries';
import { DefaultRoute } from '../../../../core/components/GateWay/GateWay';

const GrayRegularTitle = styled(RegularTitle)`
  color: ${palettes.brand.gray[700]};
`;

export const UploadDocumentsPage: React.FunctionComponent = () => {
  /**
   * Routes Matching
   */
  const { params } = useRouteMatch<{ id: string }>();

  /**
   * State
   */
  const [documentsToUpload, setDocumentsToUpload] = useState<DocumentToUpload[]>([]);
  const [documentTypeDictionary, setUpdateDocumentType] = useState<Dictionary<AgFinancingDocumentType>>({});
  const [isReadyToUpload, setIsReadyToUpload] = useState<boolean>(false);

  /**
   * Mutations
   */
  const [createAgFinancingFileFn] = useMutation(createAgFinancingFile);

  /**
   * History
   */
  const history = useHistory();

  // Update upload button state
  useEffect(() => {
    setIsReadyToUpload(!!documentsToUpload.length && documentsToUpload.every(d => !!d.documentType));
  }, [documentsToUpload]);

  const updateDocumentsToUpload = (files: File[]) => {
    const documents: DocumentToUpload[] = files.map(file => ({
      // Add name to avoid collisions when two files are uploaded simultaneously
      id: `${new Date().getTime()}-${file.name}`,
      file: file as File
    }));
    setDocumentsToUpload([...documentsToUpload, ...documents]);
  };

  const removeDocumentToUpload = (documentsToUpload: DocumentToUpload[], fileIdToRemove: string) =>
    documentsToUpload.filter(doc => doc.id !== fileIdToRemove);

  const handleUpdateDocumentType = (fileId: string, documentType: string | null) => {
    setUpdateDocumentType({ ...documentTypeDictionary, [fileId]: documentType as AgFinancingDocumentType });
  };

  const handleTableAction = (action: TableActions<DocumentToUploadColumnId> | UploadDocumentsCustomActions) => {
    if (action.type === TABLE_ACTIONS.REMOVE_DOCUMENT_TO_UPLOAD && action.payload) {
      const docsToUpload = removeDocumentToUpload(documentsToUpload, action.payload);
      return setDocumentsToUpload(docsToUpload);
    }

    if (action.type === TABLE_ACTIONS.SET_DOCUMENT_TO_UPLOAD_TYPE && action.payload) {
      const { id, documentType } = action.payload;
      const docsToUpload = documentsToUpload.map(doc => {
        if (doc.id === id) {
          return { ...doc, documentType: documentType };
        }
        return doc;
      });

      handleUpdateDocumentType(id, documentType);
      return setDocumentsToUpload([...docsToUpload]);
    }
  };

  const formatFailedToUploadFileNames = (rejected: RejectedPromiseReason[]): string =>
    rejected.map(r => ('documentName' in r ? `º${r.documentName}\n` : '')).join('');

  const { addFlashMessage } = useFlashMessage();
  const uploadFiles = () => {
    history.push(`${DefaultRoute.BR}/${params.id}/documents`);

    const filesToAg = uploadFilesToAgFinancing({
      agFinancingId: params.id,
      folder: AgFinancingFileFolder.PHASE_2
    });

    filesToAg(
      documentsToUpload.map(d => {
        const tr = d.file;
        tr['newFileId'] = d.id;

        return tr;
      }),
      AgFinancingFilesContext.DOCUMENTS,
      documentTypeDictionary
    )
      .then(filesUploaded => {
        const { fulfilled, rejected } = filesUploaded;
        const filesUploadedWithAllInfo = fulfilled.map(file => ({
          ...file,
          agFinancingId: params.id,
          status: AgFinancingFileStatus.SENT
        }));

        if (rejected.length) {
          addFlashMessage({
            content: formatFailedToUploadFileNames(rejected),
            title: text.firstLetterUppercase(i18next.t('documentsFailedToUpload')),
            variant: 'error'
          });
        }

        if (fulfilled.length) {
          createAgFinancingFileFn({
            variables: {
              data: filesUploadedWithAllInfo
            }
          }).then(() => {
            addFlashMessage({
              content: text.firstLetterUppercase(i18next.t('documentsUploaded')),
              title: '',
              variant: 'success'
            });
          });
        }
      })
      .catch(() => {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('uploadFilesToAgFinancingError')),
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <LoadingWrapper
      query={negotiationInformation}
      queryOptions={{
        variables: {
          agFinancingId: params.id
        }
      }}
    >
      {(result: QueryResult<{ financingAgFinancing: AgFinancing }>) => {
        const negotiation = result.data?.financingAgFinancing;

        if (result.error || !negotiation) {
          return (
            <NewPage
              backLink="/br-growers"
              backLinkLabel={text.toTitleCase(i18next.t('growers'))}
              title={text.firstLetterUppercase(i18next.t('addDocumentsTo', { entity: 'negotiation' }))}
            >
              <Body className="mt-1">
                {text.firstLetterUppercase(i18next.t('noNegotiationFoundBackToNegotiationList'))}
              </Body>
            </NewPage>
          );
        }

        return (
          <NewPage
            backLink={`/br-growers/${negotiation.id}/documents`}
            backLinkLabel={text.toTitleCase(i18next.t('negotiationDetails'))}
            title={text.firstLetterUppercase(
              i18next.t('addDocumentsTo', { entity: negotiation.grower?.name || 'negotiation' })
            )}
          >
            <ContentCard titleText={i18next.t('uploadDocuments')}>
              <GrayRegularTitle>{text.firstLetterUppercase(i18next.t('selectFilesToUpload'))}</GrayRegularTitle>
              <SmallBody className="mt-1">
                {text.firstLetterUppercase(i18next.t('acceptedFileTypes', { fileTypes: 'pdf, jpeg, gif, tiff, png' }))}.{' '}
                {text.firstLetterUppercase(i18next.t('maxFileSize', { maxSize: '50 MB' }))}
              </SmallBody>
              <UploadDocuments onNewFiles={updateDocumentsToUpload} />
              <DocumentsToUploadList documentsToUpload={documentsToUpload} onTableAction={handleTableAction} />
              <Button className="mt-4" onClick={uploadFiles} disabled={!isReadyToUpload}>
                {text.toTitleCase(i18next.t('upload'))}
              </Button>
            </ContentCard>
          </NewPage>
        );
      }}
    </LoadingWrapper>
  );
};
